import { FleetAreaCode, formatCentAsEuro } from '@gb-ticket/shared-types';
import { useMemo, useState } from 'react';
import { Link } from 'react-router';

import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { usePageTitle } from '@/hooks/use-page-title';
import { PlatformPageHeader } from '@/platform/components/platform-page-header';
import {
  fleetPanelClassName,
  fleetTableFrameClassName,
} from '@/products/fleet/features/fleet-panel';
import { fleetAreaOn, useFleetAreas } from '@/products/fleet/features/use-fleet-areas';
import { useFleetCostMonth } from '@/products/fleet/features/use-fleet-fuel';
import { fleetVehiclePath } from '@/products/fleet/fleet-paths';

function currentMonthYm(): string {
  const now = new Date();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  return `${now.getFullYear()}-${month}`;
}

export function FleetBetriebKostenPage() {
  usePageTitle('Betrieb — Kosten');
  const areas = useFleetAreas();
  const catalog = areas.isError ? undefined : areas.data;
  const workshopOn = fleetAreaOn(catalog, FleetAreaCode.WORKSHOP);
  const fuelOn = fleetAreaOn(catalog, FleetAreaCode.FUEL);
  const [month, setMonth] = useState(currentMonthYm);
  const costs = useFleetCostMonth(month);
  const rows = useMemo(() => costs.data?.items ?? [], [costs.data]);
  const description =
    workshopOn && fuelOn
      ? 'Werkstatt und Kraftstoff je Fahrzeug im gewählten Monat.'
      : workshopOn
        ? 'Werkstatt je Fahrzeug im gewählten Monat.'
        : 'Kraftstoff je Fahrzeug im gewählten Monat.';

  return (
    <div className="space-y-6">
      <PlatformPageHeader title="Kosten" description={description} />
      <div className={`${fleetPanelClassName} max-w-xs`}>
        <Label htmlFor="cost-month">Monat</Label>
        <Input
          id="cost-month"
          className="mt-2"
          type="month"
          value={month}
          onChange={(event) => setMonth(event.target.value)}
        />
      </div>
      {costs.isPending ? (
        <p className="text-sm text-muted-foreground">Kosten werden geladen …</p>
      ) : null}
      {rows.length === 0 && !costs.isPending ? (
        <p className="text-sm text-muted-foreground">In diesem Monat keine Kosten.</p>
      ) : null}
      {rows.length > 0 ? (
        <div className={fleetTableFrameClassName}>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Kennzeichen</TableHead>
                {workshopOn ? <TableHead>Werkstatt</TableHead> : null}
                {fuelOn ? <TableHead>Kraftstoff</TableHead> : null}
                <TableHead>Summe</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((row) => (
                <TableRow key={row.vehicleId}>
                  <TableCell>
                    <Link
                      className="font-medium hover:underline"
                      to={fleetVehiclePath(row.vehicleId)}
                    >
                      {row.licensePlate}
                    </Link>
                  </TableCell>
                  {workshopOn ? <TableCell>{formatCentAsEuro(row.workshopCents)}</TableCell> : null}
                  {fuelOn ? <TableCell>{formatCentAsEuro(row.fuelCents)}</TableCell> : null}
                  <TableCell className="font-medium">
                    {formatCentAsEuro(
                      (workshopOn ? row.workshopCents : 0) + (fuelOn ? row.fuelCents : 0),
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      ) : null}
    </div>
  );
}
